<template>
  <div class="page-wrapper">
    <div class="d-flex align-center pa-4">
      <h1 class="text-h6">
        {{ $t('upload-image') }}
      </h1>
    </div>

    <div v-for="imgType in imageTypes" :key="imgType.name" class="pt-7">
      <div :class="[{ 'd-flex pl-4': $vuetify.breakpoint.smAndUp }, 'label']">
        <p>{{ $t(`multiple_${imgType.name}_drop_zone_message`) }}</p>
        <v-spacer />
        <v-btn-toggle v-model="sortingMode" class="mr-3" mandatory>
          <v-btn :value="false" small>
            {{ $t('upload_image') }}
          </v-btn>

          <v-btn :value="true" small>
            {{ $t('change_sorting') }}
          </v-btn>
        </v-btn-toggle>
      </div>

      <BaseFileDropZone
        :disabled="sortingMode"
        accept=".jpeg,.jpg,.bmp,.png,.gif"
        multiple
        @drop="onImageUpload($event, imgType.name)"
      >
        <draggable
          :value="images[imgType.name]"
          :disabled="!sortingMode"
          animation="150"
          class="drop-zone"
          @end="onOrderChange($event, imgType.name)"
        >
          <div v-for="img in images[imgType.name]" :key="img.id">
            <div class="pa-2">
              <v-img
                :src="$backendUrl(img.image_path)"
                :alt="img.original_name"
                width="100px"
                height="100px"
                class="pages-img clickable"
                @click.stop="onImageView(img)"
              >
                <v-icon v-if="sortingMode" x-large class="image-icon draggable">mdi-drag</v-icon>
              </v-img>
              <div class="d-flex justify-space-between name-box">
                <p :class="{ draggable: sortingMode }" class="image-name text-truncate">
                  {{ img.original_name }}
                </p>
                <v-btn icon @click.stop="onImageDelete(img, imgType.name)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </draggable>
      </BaseFileDropZone>
    </div>

    <div class="pt-7">
      <div class="label pl-4">
        <p v-if="!hasTrainingImg">{{ $t('training_image_upload_message') }}</p>
        <p v-else>{{ $t('training_image_replace_message') }}</p>
      </div>
      <BaseFileDropZone
        v-if="!hasTrainingImg"
        accept=".jpeg,.jpg,.bmp,.png,.gif"
        @drop="onImageUpload($event, 'training')"
      >
        <div class="drop-zone"></div>
      </BaseFileDropZone>

      <div v-for="img in images['training']" :key="img.id">
        <div
          v-if="img.type === 'training'"
          :class="$vuetify.breakpoint.smAndDown ? 'pl-3' : 'pl-6'"
        >
          <v-img
            :src="$backendUrl(img.image_path)"
            :alt="img.original_name"
            width="100px"
            height="100px"
            @click.stop="onImageView(img, img.type)"
          />
          <div v-if="img.type === 'training'" class="d-flex justify-space-between name-box">
            <p class="image-name text-truncate">
              {{ img.original_name }}
            </p>
            <v-btn icon @click="onImageDelete(img, img.type)"><v-icon>mdi-delete</v-icon></v-btn>
          </div>
        </div>
      </div>
    </div>
    <router-view @click:close="onCloseDialog" />
  </div>
</template>

<script>
import draggable from 'vuedraggable';
import { mapErrorsToInputs } from '@/util/forms.js';
import { mapActions, mapState } from 'vuex';
import eventBus, { OPEN_SNACKBAR } from '@/util/event-bus.js';
import BaseFileDropZone from '../../components/base/BaseFileDropZone';

export default {
  name: 'ImageUpload',

  components: { BaseFileDropZone, draggable },

  data() {
    return {
      sortingMode: false,
      imageTypes: [{ name: 'partner_logo' }, { name: 'friend_logo' }, { name: 'slider' }],
    };
  },

  computed: {
    ...mapState('uploadImage', ['images']),

    hasTrainingImg() {
      return !!this.images.training?.length;
    },
  },

  created() {
    this.fetchImages({ in_homepage: true });
  },

  methods: {
    ...mapActions('uploadImage', ['uploadImages', 'reorderImages', 'fetchImages', 'deleteImage']),

    onOrderChange(event, type) {
      this.reorderImages({ oldIndex: event.oldIndex, newIndex: event.newIndex, type });
    },

    onImageUpload(images, type) {
      this.uploadImages({ images, type }).catch((err) => {
        const errors = mapErrorsToInputs(err);
        let snackbarError = '';
        Object.values(errors).forEach((errorArray) => {
          for (let i = 0; i < errorArray.length; i++) {
            if (snackbarError.length) {
              snackbarError += `; \n${errorArray[i]}`;
            } else {
              snackbarError += errorArray[i];
            }
          }
        });
        eventBus.$emit(OPEN_SNACKBAR, snackbarError);
        throw err;
      });
    },

    onImageView(image) {
      if (this.sortingMode) {
        return;
      }
      this.$router.push({ name: 'uploadImages/images', params: { url: image.image_path } });
    },

    onImageDelete(image, type) {
      this.deleteImage({ image, type });
    },

    onCloseDialog() {
      this.$router.push({ name: 'uploadImages' });
    },
  },
};
</script>

<style scoped>
.drop-zone {
  background-color: white;
  outline: 2px dashed darkgray;
  outline-offset: 10px;
  width: 97%;
  height: auto;
  min-height: 120px;
  text-align: center;
  margin: auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}
.label {
  flex: none;
  margin-bottom: 20px;
  background-color: white;
  z-index: 200;
}
.label p,
.image-name {
  color: darkgray;
  margin: 0;
}
.pages-img {
  position: relative;
  margin-top: 16px;
}
.name-box {
  width: 120px;
}
</style>
